import { Box, IconButton, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import type { Period, TimeSlot } from '@schooly/api';
import { PeriodTypeEnum } from '@schooly/api';
import {
  AttentionTooltipIcon,
  createTimeSlotColorPresets,
  EditIcon,
  ItemRoot,
  ItemTime,
  ItemTitle,
  PlusIcon,
  SimpleButton,
  TimeTablingColorPreset,
  TWO_TONE_CLASSNAME,
  TwoToneBackground,
  TypographyWithOverflowHint,
  useTimeTablingColors,
} from '@schooly/style';
import { FC, PropsWithChildren, ReactNode } from 'react';
import React from 'react';
import { useIntl } from 'react-intl';

import { RegisterSessionEnum } from '@schooly/api';
import { hasAgeGroupOutsideScope } from '../../../Groups/utils';
import { TimetableGroup } from '../types';
import { PERIOD_TYPE_COLOR_PRESETS, splitGroups } from '../utils';
import { SIMPLE_SLOT_HEIGHT, TIME_COLUMN_WIDTH } from './TimetableGrid';
import { useGroupConflict } from './TimetableGroupConflictsContext';

const GROUP_CELL_MIN_HEIGHT = 114;

type TimetableSlotCardProps = {
  timeSlot: TimeSlot;
  period: Period;
  registerSession?: RegisterSessionEnum | null;
  groups: TimetableGroup[];
  structureAgeGroupIds: string[];
  noFilterMatches?: boolean;
  groupRoomMap?: Record<string, string>;
  onSlotClick: (timeSlotId: string) => void;
  isEditable: boolean;
};

const MAX_VISIBLE_GROUPS = 3;

export const ASSIGNABLE_PERIODS = [PeriodTypeEnum.Lesson, PeriodTypeEnum.Registration];

export const TimetableSlotCard: FC<TimetableSlotCardProps> = ({
  timeSlot,
  period,
  registerSession,
  groups,
  structureAgeGroupIds,
  noFilterMatches,
  groupRoomMap,
  onSlotClick,
  isEditable,
}) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const isAssignable = ASSIGNABLE_PERIODS.includes(period.type);
  const { resolveColorConfig } = useTimeTablingColors(
    createTimeSlotColorPresets(theme, groups.length > 0 || !!noFilterMatches),
  );

  const periodColor = PERIOD_TYPE_COLOR_PRESETS[period.type];
  const colorConfig = resolveColorConfig(periodColor);

  const isLessonRegister = !!registerSession && period.type === PeriodTypeEnum.Lesson;

  const displayTitle =
    isLessonRegister && registerSession
      ? `${period.name} + ${formatMessage({
          id: `school-timetableStructure-registerSession-badge-${registerSession}`,
        })}`
      : period.name;

  if (!isAssignable) {
    return (
      <TimeSlotTooltip
        title={
          <Typography textAlign="center">
            {formatMessage({
              id: `school-timetableStructure-periodType-${period.type}`,
            })}{' '}
            "{period.name}"
          </Typography>
        }
      >
        <SimpleSlotCard label={period.name} colorConfig={colorConfig} />
      </TimeSlotTooltip>
    );
  }

  return (
    <AssignableSlotCard
      timeSlot={timeSlot}
      groups={groups}
      structureAgeGroupIds={structureAgeGroupIds}
      noFilterMatches={noFilterMatches}
      groupRoomMap={groupRoomMap}
      onSlotClick={onSlotClick}
      isEditable={isEditable}
      colorConfig={colorConfig}
      secondColorConfig={
        isLessonRegister ? resolveColorConfig(PERIOD_TYPE_COLOR_PRESETS['Registration']) : undefined
      }
      displayTitle={displayTitle}
    />
  );
};

type SimpleSlotCardProps = {
  label: string;
  colorConfig: TimeTablingColorPreset;
};

const SimpleSlotCard: FC<PropsWithChildren<SimpleSlotCardProps>> = ({
  label,
  colorConfig,
  children,
}) => {
  return (
    <ItemRoot
      colorConfig={colorConfig}
      sx={{
        height: SIMPLE_SLOT_HEIGHT,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <ItemTitle variant="h4">{label}</ItemTitle>
      {children}
    </ItemRoot>
  );
};

type AssignableSlotCardProps = {
  timeSlot: TimeSlot;
  groups: TimetableGroup[];
  structureAgeGroupIds: string[];
  noFilterMatches?: boolean;
  groupRoomMap?: Record<string, string>;
  onSlotClick: (timeSlotId: string) => void;
  isEditable: boolean;
  colorConfig: TimeTablingColorPreset;
  secondColorConfig?: TimeTablingColorPreset;
  twoToneBackground?: boolean;
  displayTitle: string;
};

const AssignableSlotCard: FC<AssignableSlotCardProps> = ({
  timeSlot,
  groups,
  structureAgeGroupIds,
  noFilterMatches,
  groupRoomMap,
  onSlotClick,
  isEditable,
  colorConfig,
  secondColorConfig,
  displayTitle,
}) => {
  const { formatMessage } = useIntl();
  const { getGroupConflict } = useGroupConflict();
  const hasGroups = groups.length > 0;
  const isPopulated = hasGroups || !!noFilterMatches;
  const showAddState = !isPopulated;
  const { visible: visibleGroups, hiddenCount: hiddenGroupsCount } = splitGroups(
    groups,
    MAX_VISIBLE_GROUPS,
  );

  const handleClick = () => {
    if (isEditable) {
      onSlotClick(timeSlot.id);
    }
  };

  const navigateToGroup = (groupId: string) => {
    window.open(`/groups/${groupId}`, '_blank');
  };

  const renderMainContent = () => {
    if (!hasGroups) {
      if (noFilterMatches) {
        return (
          <Typography variant="caption" color="common.grey2" textAlign="center">
            {formatMessage({ id: 'timetable-NoGroupsMatchingFilters' })}
          </Typography>
        );
      }

      return isEditable ? (
        <SimpleButton size="small" startIcon={<PlusIcon fontSize="small" />} onClick={handleClick}>
          {formatMessage({ id: 'groups-AddGroups' })}
        </SimpleButton>
      ) : null;
    }

    return (
      <Stack sx={{ width: '100%' }}>
        {visibleGroups.map((group, index) => {
          const isLastGroup = index === visibleGroups.length - 1;
          const roomName = groupRoomMap?.[group.id];

          const groupHasAgeGroupMismatch = hasAgeGroupOutsideScope(
            group.age_groups,
            structureAgeGroupIds,
          );
          const groupConflict = getGroupConflict(group.id);
          return (
            <Box
              key={group.id}
              sx={(theme) => ({
                width: '100%',
                borderBottom:
                  isLastGroup && !hiddenGroupsCount
                    ? 'none'
                    : `1px solid ${theme.palette.common.white}`,
                mb: 0.5,
                pb: 0.25,
              })}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center" gap={0.5}>
                <Stack direction="row" alignItems="center" gap={0.25} sx={{ minWidth: 0 }}>
                  {groupHasAgeGroupMismatch && (
                    <AttentionTooltipIcon
                      title={formatMessage({ id: 'timetable-GroupAgeGroupNotMatching' })}
                      severity="error"
                      size="small"
                    />
                  )}
                  {!!groupConflict && (
                    <AttentionTooltipIcon
                      title={formatMessage(
                        { id: 'timetable-group-conflict-Warning' },
                        { name: groupConflict.timetableName },
                      )}
                      severity="warning"
                      size="small"
                    />
                  )}
                  <ItemTitle onClick={() => navigateToGroup(group.id)}>{group.name}</ItemTitle>
                </Stack>
                {roomName && (
                  <ItemTime sx={{ flexShrink: 0, maxWidth: '40px' }}>{roomName}</ItemTime>
                )}
              </Stack>
            </Box>
          );
        })}
        {hiddenGroupsCount > 0 && (
          <TimeSlotTooltip
            title={
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr auto',
                  alignItems: 'center',
                  gap: 0.5,
                  columnGap: 1,
                }}
              >
                {groups.map((g) => {
                  const roomName = groupRoomMap?.[g.id];
                  const groupHasAgeGroupMismatch = hasAgeGroupOutsideScope(
                    g.age_groups,
                    structureAgeGroupIds,
                  );
                  const groupConflict = getGroupConflict(g.id);
                  return (
                    <React.Fragment key={g.id}>
                      <Stack direction="row" alignItems="center" gap={0.25} sx={{ minWidth: 0 }}>
                        {groupHasAgeGroupMismatch && (
                          <AttentionTooltipIcon
                            title={formatMessage({ id: 'timetable-GroupAgeGroupNotMatching' })}
                            severity="error"
                            size="small"
                          />
                        )}
                        {!!groupConflict && (
                          <AttentionTooltipIcon
                            title={formatMessage(
                              { id: 'timetable-group-conflict-Warning' },
                              { name: groupConflict.timetableName },
                            )}
                            severity="warning"
                            size="small"
                          />
                        )}
                        <ItemTitle onClick={() => navigateToGroup(g.id)}>{g.name}</ItemTitle>
                      </Stack>
                      <TypographyWithOverflowHint
                        variant="caption"
                        sx={{
                          color: roomName ? 'common.grey2' : 'common.grey',
                          maxWidth: '40px',
                        }}
                      >
                        {roomName ?? '-'}
                      </TypographyWithOverflowHint>
                    </React.Fragment>
                  );
                })}
              </Box>
            }
          >
            <ItemTitle>+{hiddenGroupsCount}</ItemTitle>
          </TimeSlotTooltip>
        )}
      </Stack>
    );
  };

  return (
    <ItemRoot
      colorConfig={colorConfig}
      onClick={showAddState ? handleClick : undefined}
      sx={{
        height: '100%',
        overflow: 'hidden',
        position: 'relative',
        cursor: showAddState ? 'pointer' : 'default',
        minHeight: GROUP_CELL_MIN_HEIGHT,
        maxHeight: GROUP_CELL_MIN_HEIGHT,
        [TWO_TONE_CLASSNAME]: {
          path: {
            fill: secondColorConfig ? secondColorConfig.bg : undefined,
          },
        },
        '&:hover': {
          '.edit-icon-button': {
            visibility: isPopulated ? 'visible' : 'hidden',
          },
          [TWO_TONE_CLASSNAME]: {
            path: {
              fill: secondColorConfig ? secondColorConfig.bgHover : undefined,
            },
          },
        },
        border: (theme) => (showAddState ? `1px solid ${theme.palette.common.main5}` : 'none'),
      }}
    >
      {!!secondColorConfig && <TwoToneBackground />}
      <Stack
        direction="row"
        alignItems="center"
        position="relative"
        justifyContent="center"
        sx={{
          py: 0.5,
          borderBottom: hasGroups ? '1px solid white' : 'none',
        }}
      >
        <ItemTitle maxWidth={TIME_COLUMN_WIDTH - 4} py={0.25} variant="h4">
          {displayTitle}
        </ItemTitle>

        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            handleClick();
          }}
          className="edit-icon-button"
          sx={{
            visibility: 'hidden',
            position: 'absolute',
            right: 4,
            top: 4,
            display: isEditable && isPopulated ? 'flex' : 'none',
          }}
        >
          <EditIcon fontSize="medium" />
        </IconButton>
      </Stack>

      <Box
        sx={{
          flex: 1,
          px: 0.5,
          pt: 0.5,
          pb: 0.75,
          overflow: 'hidden',
          display: 'flex',
          position: 'relative',
          alignItems: hasGroups ? 'flex-start' : 'center',
        }}
      >
        {renderMainContent()}
      </Box>
    </ItemRoot>
  );
};

const TimeSlotTooltip: FC<PropsWithChildren<{ title: ReactNode }>> = ({ children, title }) => {
  return (
    <Tooltip
      componentsProps={{
        tooltip: {
          sx: {
            width: 200,
            padding: 1.25,
          },
        },
      }}
      title={title}
    >
      <Box component="span">{children}</Box>
    </Tooltip>
  );
};
